import type { IAdminSecAdvFirmDetail, ISecAdvCatalogue } from '../../api/sec-adv-contracts';
import { isObject } from '../../components/sec-adv-values';
import { clientTypes, type IClientType } from './charts';
import { ITEM_5E, ITEM_5G, elementAt } from './register';

/**
 * A firm's figures as the firm page reads them (SR-423): survey-api's summary first, the record
 * through the catalogue's paths where an older survey-api's summary lacks a member.
 */

/** The element at a dotted path in the record — `FormInfo.Part1A.Item5F`. */
export function recordAt(record: unknown, path: string): unknown {
  let at: unknown = record;
  for (const name of path.split('.')) {
    if (!isObject(at)) return undefined;
    at = at[name];
  }
  return at;
}

/** One attribute's value as the feed wrote it, or null. */
function recordValue(record: unknown, key: string): Nullable<string> {
  const value = recordAt(record, key);
  return typeof value === 'string' && value !== '' ? value : null;
}

function wholeOf(value: Nullable<string>): Nullable<number> {
  return value !== null && /^\d+$/.test(value) ? Number(value) : null;
}

/** The yes-or-no questions of an element answered `Y`, in the catalogue's order. */
export function answeredYes(
  record: unknown,
  catalogue: Nullable<ISecAdvCatalogue>,
  path: string
): string[] {
  const element = recordAt(record, path);
  if (!isObject(element)) return [];
  const listed = elementAt(catalogue, path)?.attributes.map((attribute) => attribute.name);
  const names = listed ?? Object.keys(element);
  return names.filter((name) => element[name] === 'Y');
}

export interface IFirmFigures {
  totalAum: Nullable<number>;
  discretionaryAum: Nullable<number>;
  nonDiscretionaryAum: Nullable<number>;
  totalAccounts: Nullable<number>;
  discretionaryAccounts: Nullable<number>;
  nonDiscretionaryAccounts: Nullable<number>;
  employees: Nullable<number>;
  advisoryEmployees: Nullable<number>;
  clients: Nullable<number>;
  otherOffices: Nullable<number>;
  services: string[];
  servicesOther: Nullable<string>;
  compensation: string[];
  compensationOther: Nullable<string>;
  types: IClientType[];
  isEra: boolean;
}

export function figuresOf(
  firm: IAdminSecAdvFirmDetail,
  catalogue: Nullable<ISecAdvCatalogue>
): IFirmFigures {
  const { summary, record } = firm;
  const types = clientTypes(summary.clientsByType, catalogue);
  const counted = types.reduce((sum, type) => sum + type.count, 0);
  return {
    totalAum: summary.raum?.total ?? null,
    discretionaryAum: summary.raum?.discretionary ?? null,
    nonDiscretionaryAum: summary.raum?.nonDiscretionary ?? null,
    totalAccounts: summary.raum?.totalAccounts ?? null,
    discretionaryAccounts: summary.raum?.discretionaryAccounts ?? null,
    nonDiscretionaryAccounts: summary.raum?.nonDiscretionaryAccounts ?? null,
    employees: summary.employees,
    advisoryEmployees:
      summary.advisoryEmployees !== undefined
        ? summary.advisoryEmployees
        : wholeOf(recordValue(record, 'FormInfo.Part1A.Item5B.Q5B1')),
    clients:
      summary.clients !== undefined ? summary.clients : types.length > 0 ? counted : null,
    otherOffices:
      summary.otherOffices !== undefined
        ? summary.otherOffices
        : wholeOf(recordValue(record, 'FormInfo.Part1A.Item1.Q1F5')),
    services: summary.services ?? answeredYes(record, catalogue, ITEM_5G),
    servicesOther:
      summary.servicesOther !== undefined
        ? summary.servicesOther
        : recordValue(record, `${ITEM_5G}.Q5G12Oth`),
    compensation: summary.compensation ?? answeredYes(record, catalogue, ITEM_5E),
    compensationOther:
      summary.compensationOther !== undefined
        ? summary.compensationOther
        : recordValue(record, `${ITEM_5E}.Q5E7Oth`),
    types,
    isEra: firm.firmType === 'ERA'
  };
}

export const ITEM_7A = 'FormInfo.Part1A.Item7A';

/** "Has a related person that is a trust company" → "Trust company". */
export function relatedPersonType(label: string): string {
  const short = label.replace(/^Has a related person that is (an? |another )?/, '');
  return short.charAt(0).toUpperCase() + short.slice(1);
}

/** Item 11's questions as short pills — RIA Pulse's words for them. */
export const DISCLOSURE_PILLS: Record<string, string> = {
  Q11A1: 'Felony Conviction',
  Q11A2: 'Felony Charge',
  Q11B1: 'Misdemeanor Conviction',
  Q11B2: 'Misdemeanor Charge',
  Q11C1: 'SEC/CFTC False Statement Finding',
  Q11C2: 'SEC/CFTC Violation Finding',
  Q11C3: 'SEC/CFTC Cause of Restriction',
  Q11C4: 'SEC/CFTC Order Entry',
  Q11C5: 'SEC/CFTC Penalty/Cease-and-Desist',
  Q11D1: 'Other Regulator False Statement Finding',
  Q11D2: 'Other Regulator Violation Finding',
  Q11D3: 'Other Regulator Cause of Restriction',
  Q11D4: 'Other Regulator Order (Past 10 Years)',
  Q11D5: 'Other Regulator Registration Denied/Revoked',
  Q11E1: 'SRO False Statement Finding',
  Q11E2: 'SRO Rule Violation Finding',
  Q11E3: 'SRO Cause of Restriction',
  Q11E4: 'SRO Discipline',
  Q11F: 'Professional Authorization Revoked',
  Q11G: 'Pending Regulatory Proceeding',
  Q11H1A: 'Court Injunction',
  Q11H1B: 'Court Violation Finding',
  Q11H1C: 'Civil Action Settled',
  Q11H2: 'Pending Civil Proceeding'
};
